import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { HiArrowLeft, HiCheckCircle } from 'react-icons/hi';
import useConfigPanel from 'utils/hooks/useConfigPanel';
import usePostGenerator from 'utils/hooks/usePostGenerator';
import { PostArticleContext } from 'contexts/PostArticleContext';

const PostTypeSelector = ({ onBack, onClose, isDarkMode }) => {
    const { postTypes } = useConfigPanel();
    const { generateEmptyPost } = usePostGenerator();
    const { openPostArticle } = useContext(PostArticleContext);

    const handlePostTypeSelect = (postType) => {
        // Generate empty post and open the post editor
        const newPost = generateEmptyPost(postType.value);
        openPostArticle(newPost);
        
        // Close the modal
        onClose();
    };

    return (
        <div className="p-8 md:p-12 min-h-[600px]">
            {/* Back Button */}
            <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                whileHover={{ scale: 1.05, x: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className="flex items-center gap-2 mb-8 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors duration-200 backdrop-blur-xl border border-white/10"
            >
                <HiArrowLeft className="text-xl" />
                <span className="font-medium">Back</span>
            </motion.button>

            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-center mb-10"
            >
                <h1 className="text-4xl font-bold mb-3 text-white">
                    Choose Post Type
                </h1>
                <p className="text-lg text-gray-300">
                    Select the social network for your post
                </p>
            </motion.div>

            {/* Post Type Cards Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
                {postTypes?.map((postType, index) => (
                    <motion.div
                        key={postType.value}
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{
                            duration: 0.3,
                            delay: index * 0.05,
                            type: 'spring',
                            stiffness: 400,
                            damping: 25,
                        }}
                        whileHover={{
                            scale: 1.05,
                            transition: { duration: 0.2 },
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handlePostTypeSelect(postType)}
                        className="relative cursor-pointer group"
                    >
                        {/* Card Container */}
                        <div className="relative overflow-hidden rounded-2xl p-6 h-full flex flex-col items-center justify-center transition-all duration-300 bg-white/5 backdrop-blur-xl hover:bg-white/10 shadow-xl hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] border border-white/10">
                            {/* Decorative Background */}
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            
                            {/* Floating shapes */}
                            <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full transform translate-x-8 -translate-y-8 blur-2xl" />
                            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full transform -translate-x-6 translate-y-6 blur-xl" />

                            {/* Content */}
                            <div className="relative z-10 flex flex-col items-center gap-3 w-full">
                                {/* Network Icon/Emoji */}
                                <div className="w-12 h-12 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                                    <div className="w-full h-full flex items-center justify-center">
                                        <img src={postType.imgUrl} alt={postType.name} className={`w-full h-full object-contain ${postType.imgDarkClassName ? `${postType.imgDarkClassName}` : ''}`} />
                                    </div>
                                </div>

                                {/* Network Name */}
                                <span className="text-center font-semibold text-sm text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-indigo-400 group-hover:via-purple-400 group-hover:to-pink-400 group-hover:bg-clip-text transition-all duration-300">
                                    {postType.name}
                                </span>
                            </div>

                            {/* Hover Indicator */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0 }}
                                whileHover={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.2 }}
                                className="absolute top-3 right-3"
                            >
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
                                    <HiCheckCircle className="text-white text-base" />
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

export default PostTypeSelector;

